import {useContext, useEffect} from "react";
import {GenericComponentModel, SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {useRecoilState} from "recoil";
import {ancillaryListAtom} from "../../../state/ancillariesRecoil";
import {useTranslation} from "react-i18next";
import SearchFilters from "../../../common/components/SearchFilters";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {FormSearchAncillaryExceptionModel} from "../../../api/types/ancillaryException";

interface AncillaryExceptionFiltersProps {
    handleClickSearch: () => void;
    handleClickResetSearch: () => void;
    commercialCategoryListOriginal: CommercialCategorySelectFE[];
    testFilters: FormSearchAncillaryExceptionModel;
    setTestFilters: (filters: FormSearchAncillaryExceptionModel) => void;
}

const AncillaryExceptionFilters = ({
                                       handleClickSearch,
                                       handleClickResetSearch,
                                       commercialCategoryListOriginal,
                                       testFilters,
                                       setTestFilters
                                   }: AncillaryExceptionFiltersProps) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [ancillaryList] = useRecoilState(ancillaryListAtom);

    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const dealerOptions: SelectElementModelFE[] = user.dealers.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    // Filtra commercial categories quando cambiano legal entities o commercial segment
    useEffect(() => {
        // Se nessuna legal entity selezionata, svuota la lista
        if (!testFilters.legalEntities || 
            (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0)) {
            setCommercialCategoryList([]);
            return;
        }

        // Ottieni le legal entities selezionate
        const selectedLegalEntities = Array.isArray(testFilters.legalEntities)
            ? (testFilters.legalEntities as SelectElementModelFE[]).map((le: SelectElementModelFE) => le.code as string)
            : [(testFilters.legalEntities as SelectElementModelFE).code as string];

        // Filtra per commercial segment se selezionato
        let filteredBySegment = commercialCategoryListOriginal;
        if (testFilters.commercialSegmentId) {
            filteredBySegment = commercialCategoryListOriginal.filter(
                cc => cc.commercialSegment.id === (testFilters.commercialSegmentId as SelectElementModelFE).code
            );
        }

        // Espandi le categorie in base alle legal entities selezionate e deduplica per label
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        
        filteredBySegment.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (selectedLegalEntities.includes(labelObj.country)) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });

        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [testFilters.legalEntities, testFilters.commercialSegmentId, commercialCategoryListOriginal]);


    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "name",
            value: testFilters.name,
            label: `${t("pricing:ancillaryException.form.labels.name")}`,
            handleChange: (value: string) => {
                setTestFilters({
                    ...testFilters,
                    name: value
                });
            },
            options: [],
            type: "TextField"
        },
        {
            name: "commercialSegmentId",
            value: testFilters.commercialSegmentId,
            label: `${t("pricing:ancillaryException.form.labels.commercialSegment")}`,
            handleChange: (value: SelectElementModelFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null
                });
            },
            options: commercialSegmentList,
            type: "Autocomplete"
        },
        {
            name: "commercialCategoryId",
            value: testFilters.commercialCategoryId,
            label: `${t("pricing:ancillaryException.form.labels.commercialCategory")}`,
            handleChange: (value: CommercialCategorySelectFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value
                });
            },
            options: commercialCategoryList,
            type: "Autocomplete",
            disabled: !testFilters.legalEntities || 
                (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0)
        }
    ];
    // Validazione: almeno una legal entity deve essere selezionata
    const legalEntitiesError = !testFilters.legalEntities || 
        (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0);

    // ITDRIV-20190 / LPP-23039: Ancillary disabilitato se LE o Dealer non entrambi selezionati
    const selectedLECount = Array.isArray(testFilters.legalEntities)
        ? (testFilters.legalEntities as SelectElementModelFE[]).filter((le) => le.code !== "all").length
        : 0;
    const selectedDealerCount = Array.isArray(testFilters.dealers)
        ? (testFilters.dealers as SelectElementModelFE[]).filter((d) => d.code !== "all").length
        : 0;
    const ancillaryDisabled = selectedLECount === 0 || selectedDealerCount === 0;

    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "reservationFrom",
            value: testFilters.reservationFrom as string,
            label: `${t("pricing:ancillaryException.form.labels.reservationFrom")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                reservationFrom: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "reservationTo",
            value: testFilters.reservationTo as string,
            label: `${t("pricing:ancillaryException.form.labels.reservationTo")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                reservationTo: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "pickupDateFrom",
            value: testFilters.pickupDateFrom as string,
            label: `${t("pricing:ancillaryException.form.labels.pickupDateFrom")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                pickupDateFrom: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "pickupDateTo",
            value: testFilters.pickupDateTo as string,
            label: `${t("pricing:ancillaryException.form.labels.pickupDateTo")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                pickupDateTo: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "ancillaryId",
            value: testFilters.ancillaryId,
            label: `${t("pricing:ancillaryException.form.labels.ancillary")}`,
            handleChange: (value: SelectElementModelFE) => {
                setTestFilters({
                    ...testFilters,
                    ancillaryId: value
                });
            },
            options: ancillaryList,
            type: "Autocomplete",
            disabled: ancillaryDisabled
        },
        {
            name: "legalEntities",
            value: testFilters.legalEntities as any,
            label: `${t("pricing:rate.form.labels.mainDefinitionLabels.legalEntity")} *`,
            handleChange: (value: SelectElementModelFE[]) => {
                setTestFilters({
                    ...testFilters, 
                    legalEntities: value,
                    commercialCategoryId: null
                });
            },
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            error: legalEntitiesError,
            helperText: legalEntitiesError ? t("translation:validation.selectAtLeastOne") : "",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            name: "dealers",
            value: testFilters.dealers as any,
            label: `${t("pricing:rate.form.labels.mainDefinitionLabels.dealer")}`,
            handleChange: (value: SelectElementModelFE[]) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    return (
        <SearchFilters
            filtersOutOfCollapse={filtersOutOfCollapse}
            filtersInCollapse={filtersInCollapse}
            handleClickSearch={handleClickSearch}
            handleClickResetSearch={handleClickResetSearch}
        />
    );
};

export default AncillaryExceptionFilters;
